import useRolePermission from "entities/auth/hooks/useRolePermission";
import { useLanguagesQuery } from "entities/translations/queries/useLanguages";
import FlexColumn from "shared/components/Layout/components/FlexColumn";
import PageHeading from "shared/components/PageHeading";
import PrimaryButton from "shared/components/PrimaryButton";
import TranslationTable from "shared/components/TranslationTable";
import { LocaleCode } from "shared/types/languages";
import UnpublishedWarn from "../../components/UnpublishedWarn";
import PageFilterBar from "./components/PageFilterBar";
import {
  DynamicTranslationsProvider,
  useDynamicTranslationPage,
} from "./context";
import { getDynamicTranslationsColumns } from "./helpers";
import { StyledAlert } from "./styled";

const DynamicTranslationsContainer = () => (
  <DynamicTranslationsProvider>
    <DynamicTranslationsPage />
  </DynamicTranslationsProvider>
);

const DynamicTranslationsPage = () => {
  const {
    tableData,
    isLoadingTranslations,
    isDisabledPublish,
    isPendingPublish,
    totalAmountUnpublishedChanges,
    isAutoTranslationLoaded,
    updatedTranslations,
    hasShownTranslateMessageError,
    autoTranslateErrorMessage,
    handleAutoTranslate,
    handlePublishChanges,
    handleUpdatedTranslations,
    setShownTranslateMessageError,
    targetLanguage,
  } = useDynamicTranslationPage();

  const writeAccess = useRolePermission("translations:write");
  const { data: languages, isLoading: isLoadingLanguages } =
    useLanguagesQuery();
  const columns = getDynamicTranslationsColumns({
    allLanguages: languages || [],
    updatedTranslations,
    handleUpdatedTranslations,
    langsToTranslate: [LocaleCode.ja, LocaleCode.ko],
    handleAutoTranslate,
    isLoading: isAutoTranslationLoaded || isLoadingLanguages,
    readOnly: !writeAccess.allowed,
    targetLanguage,
  });

  return (
    <FlexColumn>
      <PageHeading>
        {writeAccess.allowed && (
          <FlexColumn alignSelf="flex-end" position="relative">
            <PrimaryButton
              title="Publish Changes"
              size="medium"
              disabled={isDisabledPublish}
              loading={isPendingPublish}
              onClick={handlePublishChanges}
            />
            {Boolean(totalAmountUnpublishedChanges) && (
              <UnpublishedWarn totalChanges={totalAmountUnpublishedChanges} />
            )}
          </FlexColumn>
        )}
      </PageHeading>
      <PageFilterBar />
      <TranslationTable
        columns={columns}
        emptyStateMessage="No Results Found"
        data={tableData.data}
        loading={isLoadingTranslations || isAutoTranslationLoaded}
        paginationMode="server"
        sortingMode="server"
        pageSizeOptions={[25, 50, 100]}
        paginationModel={tableData.paginationModel}
        onPaginationModelChange={tableData.onPaginationModelChange}
        rowCount={tableData.rowCount}
        heightTable="78vh"
      />
      {hasShownTranslateMessageError && (
        <StyledAlert
          severity="info"
          onClose={() => {
            setShownTranslateMessageError(false);
          }}
        >
          {autoTranslateErrorMessage}
        </StyledAlert>
      )}
    </FlexColumn>
  );
};

export default DynamicTranslationsContainer;
